/**
 * useScenarioForm - Custom hook for managing scenario form state
 *
 * Consolidates the 50+ useState hooks from ScenarioEditor.js into a single
 * organized hook with clear state groupings.
 */

import { useState, useMemo, useCallback } from 'react';

/**
 * Helper to read initial tab from URL query params
 * This ensures tab state persists across component remounts
 */
function getInitialTabFromURL() {
    if (typeof window === 'undefined') return 0;
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (!tabParam) return 0;
    const tabNum = parseInt(tabParam);
    return (!isNaN(tabNum) && tabNum >= 0 && tabNum <= 6) ? tabNum : 0;
}

// Import from centralized defaults configuration
import { DEFAULT_PV_COST_PER_KW, DEFAULT_BATTERY_COST_PER_KWH, DEFAULT_ANNUAL_KWH, DEFAULT_FINANCIAL, FINANCIAL_DEFAULTS, TARIFF_DEFAULTS, GENERATOR_DEFAULTS, RESILIENCE_DEFAULTS } from '../config/defaults';

// Generator cost uses centralized default
const DEFAULT_GENERATOR_COST_PER_KW = GENERATOR_DEFAULTS.gensetSizeKw.value;

/**
 * TOU periods initial structure for custom rate builder
 * WARNING: These are placeholder values for UI initialization only.
 * Users MUST modify these values or select a tariff for accurate analysis.
 * @see config/defaults.js for documented default values
 */
const DEFAULT_TOU_PERIODS = [
    {
        id: 1,
        name: "Peak",
        energyRate: "", // Empty - user must enter
        demandRate: "",
        timeWindows: [{ start: "16:00", end: "21:00" }],
        weekdays: [1, 2, 3, 4, 5],
        months: [6, 7, 8, 9]
    },
    {
        id: 2,
        name: "Off-Peak",
        energyRate: "",
        demandRate: "",
        timeWindows: [{ start: "21:00", end: "16:00" }],
        weekdays: [1, 2, 3, 4, 5, 6, 0],
        months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    }
];

/**
 * Tiered rates initial structure for custom rate builder
 * WARNING: These are placeholder values for UI initialization only.
 * Users MUST modify these values for accurate analysis.
 */
const DEFAULT_TIERED_RATES = [
    { id: 1, name: "First Tier", energyRate: "", maxKwh: "", description: "Enter first tier threshold" },
    { id: 2, name: "Second Tier", energyRate: "", maxKwh: "", description: "Enter second tier threshold" },
    { id: 3, name: "Third Tier", energyRate: "", maxKwh: "unlimited", description: "Remaining usage" }
];

/**
 * Custom hook managing all scenario form state
 */
export function useScenarioForm() {
    // ========== Site Configuration ==========
    const [scenarioName, setScenarioName] = useState("");
    const [siteName, setSiteName] = useState("");
    const [street, setStreet] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [siteDescription, setSiteDescription] = useState("");
    const [facilityType, setFacilityType] = useState("commercial");
    const [utilityName, setUtilityName] = useState("");
    const [serviceClass, setServiceClass] = useState("commercial");
    const [offGrid, setOffGrid] = useState(false);

    // ========== Solar (PV) Configuration ==========
    const [usePv, setUsePv] = useState(false);
    const [sizingMode, setSizingMode] = useState("prescribed");
    const [pvMaxKw, setPvMaxKw] = useState("");
    const [pvCost, setPvCost] = useState(String(DEFAULT_PV_COST_PER_KW));
    const [pvExportMode, setPvExportMode] = useState("none");

    // ========== Battery (Storage) Configuration ==========
    const [useStorage, setUseStorage] = useState(false);
    const [batterySizingMode, setBatterySizingMode] = useState("prescribed");
    const [batteries, setBatteries] = useState([{ id: 1, name: "Battery 1", kw: "", kwh: "" }]);
    const [storageMaxKw, setStorageMaxKw] = useState("");
    const [storageMaxKwh, setStorageMaxKwh] = useState("");
    const [useGenLoadBank, setUseGenLoadBank] = useState(false);
    const [batteryReservePercent, setBatteryReservePercent] = useState("20");
    const [bessChargeSource, setBessChargeSource] = useState("pv_and_grid");

    // ========== Generator Configuration ==========
    const [useGenerator, setUseGenerator] = useState(false);
    const [generatorSizingMode, setGeneratorSizingMode] = useState("prescribed");
    const [generatorMaxKw, setGeneratorMaxKw] = useState("");
    const [generatorFuelCostPerGallon, setGeneratorFuelCostPerGallon] = useState("3");
    const [generatorFuelCostPerMmbtu, setGeneratorFuelCostPerMmbtu] = useState("6");
    const [generatorFuelType, setGeneratorFuelType] = useState("diesel");
    const [generatorExportMode, setGeneratorExportMode] = useState("none");
    const [generatorRunHoursLimit, setGeneratorRunHoursLimit] = useState("");
    const [generatorUptimePercent, setGeneratorUptimePercent] = useState("96.5"); // Default matches REopt CHP
    const [generatorThermalEfficiency, setGeneratorThermalEfficiency] = useState(""); // kWh/gal for diesel, kWh/MMBtu for NG
    // Multi-generator support: array of generator objects
    // Each generator: { id, name, kw, fuel_type, database_id, manufacturer, model, efficiency }
    // Aggregated by fuel type for solver: diesel → Generator, natural_gas → CHP
    // TODO: Future - individual dispatch control per generator
    const [generators, setGenerators] = useState([]);

    // ========== Fuel Tariff Configuration ==========
    const [gasTariffId, setGasTariffId] = useState(null);
    const [gasTariffData, setGasTariffData] = useState(null);

    // ========== Tariff Configuration ==========
    const [tariffs, setTariffs] = useState([]);
    const [tariffLocation, setTariffLocation] = useState(null);
    const [selectedTariffData, setSelectedTariffData] = useState(null);
    const [useManualRate, setUseManualRate] = useState(false);
    const [rateStructureType, setRateStructureType] = useState("blended");
    const [blendedEnergyRate, setBlendedEnergyRate] = useState("0.12");
    const [blendedDemandRate, setBlendedDemandRate] = useState("15.0");
    const [monthlyFixedCharge, setMonthlyFixedCharge] = useState("25.0");
    const [touPeriods, setTouPeriods] = useState(DEFAULT_TOU_PERIODS);
    const [useMonthlyRates, setUseMonthlyRates] = useState(false);
    const [monthlyFixedCharges, setMonthlyFixedCharges] = useState(Array(12).fill("25.0"));
    const [tieredRates, setTieredRates] = useState(DEFAULT_TIERED_RATES);
    const [tieredDemandRate, setTieredDemandRate] = useState("6.03");
    const [customTariffData, setCustomTariffData] = useState(null); // Structured tariff data for solver

    // ========== CA DER Charges Configuration ==========
    const [caDerConfig, setCaDerConfig] = useState(null); // California DER charges detection/config

    // ========== Export Compensation Configuration ==========
    // NEM 3.0 export compensation settings
    const [exportConfig, setExportConfig] = useState({
        enabled: false,
        mode: 'none',  // 'hourly' | 'match_import' | 'fixed' | 'none'
        rate: '',      // Used when mode = 'fixed'
        tariffId: null,
        tariffData: null,
    });

    // ========== Commercial Assumptions ==========
    // These are restored from saved scenario and passed to useCommercialAssumptions hook
    const [commercialAssumptions, setCommercialAssumptions] = useState(null);

    // ========== Deal Analysis Configuration ==========
    // ESA pricing config, CapEx config, discount rate for deal analysis tab
    const [dealAnalysisConfig, setDealAnalysisConfig] = useState(null);

    // ========== AI Extraction State ==========
    const [inputMode, setInputMode] = useState("ai_assisted");
    const [aiExtractionComplete, setAiExtractionComplete] = useState(false);
    const [aiExtractedData, setAiExtractedData] = useState(null);
    const [aiSuggestedTariffs, setAiSuggestedTariffs] = useState([]); // Store all AI-suggested tariffs

    // ========== UI State ==========
    // Initialize tab from URL to persist across component remounts
    const [tab, setTab] = useState(() => getInitialTabFromURL());
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [tempScenarioName, setTempScenarioName] = useState("");
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [modelName, setModelName] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [error, setError] = useState("");
    const [calculationMethod, setCalculationMethod] = useState('reopt');
    const [savedModels, setSavedModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState('');

    // ========== Grouped State Objects ==========

    const siteConfig = useMemo(() => ({
        scenarioName,
        siteName,
        street,
        city,
        state,
        zipCode,
        siteDescription,
        facilityType,
        utilityName,
        serviceClass,
        offGrid,
    }), [scenarioName, siteName, street, city, state, zipCode, siteDescription, facilityType, utilityName, serviceClass, offGrid]);

    const solarConfig = useMemo(() => ({
        usePv,
        sizingMode,
        pvMaxKw,
        pvCost,
        pvExportMode,
    }), [usePv, sizingMode, pvMaxKw, pvCost, pvExportMode]);

    const batteryConfig = useMemo(() => ({
        useStorage,
        batterySizingMode,
        batteries,
        storageMaxKw,
        storageMaxKwh,
        useGenLoadBank,
        batteryReservePercent,
        bessChargeSource,
    }), [useStorage, batterySizingMode, batteries, storageMaxKw, storageMaxKwh, useGenLoadBank, batteryReservePercent, bessChargeSource]);

    const generatorConfig = useMemo(() => ({
        useGenerator,
        generatorSizingMode,
        generatorMaxKw,
        generatorFuelCostPerGallon,
        generatorFuelCostPerMmbtu,
        generatorFuelType,
        generatorExportMode,
        generatorRunHoursLimit,
        generatorUptimePercent,
        generatorThermalEfficiency,
        generators, // Multi-generator array
        gasTariffId,
        gasTariffData,
    }), [useGenerator, generatorSizingMode, generatorMaxKw, generatorFuelCostPerGallon, generatorFuelCostPerMmbtu, generatorFuelType, generatorExportMode, generatorRunHoursLimit, generatorUptimePercent, generatorThermalEfficiency, generators, gasTariffId, gasTariffData]);

    const tariffConfig = useMemo(() => ({
        tariffs,
        tariffLocation,
        selectedTariffData,
        customTariffData, // Tariff data for solver
        caDerConfig, // California DER charges configuration
        exportConfig, // NEM 3.0 export compensation configuration
        useManualRate,
        rateStructureType,
        blendedEnergyRate,
        blendedDemandRate,
        monthlyFixedCharge,
        touPeriods,
        useMonthlyRates,
        monthlyFixedCharges,
        tieredRates,
        tieredDemandRate,
    }), [tariffs, tariffLocation, selectedTariffData, customTariffData, caDerConfig, exportConfig, useManualRate, rateStructureType, blendedEnergyRate, blendedDemandRate, monthlyFixedCharge, touPeriods, useMonthlyRates, monthlyFixedCharges, tieredRates, tieredDemandRate]);

    // ========== Reset Functions ==========

    const resetSolarConfig = useCallback(() => {
        setUsePv(false);
        setSizingMode("prescribed");
        setPvMaxKw("");
        setPvCost(String(DEFAULT_PV_COST_PER_KW));
        setPvExportMode("none");
    }, []);

    const resetBatteryConfig = useCallback(() => {
        setUseStorage(false);
        setBatteries([{ id: 1, name: "Battery 1", kw: "", kwh: "" }]);
        setStorageMaxKw("");
        setStorageMaxKwh("");
        setUseGenLoadBank(false);
        setBatteryReservePercent("20");
        setBessChargeSource("pv_and_grid");
    }, []);

    const resetGeneratorConfig = useCallback(() => {
        setUseGenerator(false);
        setGeneratorMaxKw("");
        setGeneratorFuelCostPerGallon("3");
        setGeneratorFuelCostPerMmbtu("6");
        setGeneratorFuelType("diesel");
        setGeneratorExportMode("none");
        setGeneratorRunHoursLimit("");
        setGeneratorUptimePercent("96.5");
        setGeneratorThermalEfficiency("");
        setGenerators([]); // Reset multi-generator array
        setGasTariffId(null);
        setGasTariffData(null);
    }, []);

    const resetTariffConfig = useCallback(() => {
        setTariffs([]);
        setTariffLocation(null);
        setSelectedTariffData(null);
        setCustomTariffData(null);
        setUseManualRate(false);
        setRateStructureType("blended");
        setBlendedEnergyRate("0.12");
        setBlendedDemandRate("15.0");
        setMonthlyFixedCharge("25.0");
        setTouPeriods(DEFAULT_TOU_PERIODS);
        setUseMonthlyRates(false);
        setMonthlyFixedCharges(Array(12).fill("25.0"));
        setTieredRates(DEFAULT_TIERED_RATES);
        setTieredDemandRate("6.03");
        setExportConfig({
            enabled: false,
            mode: 'none',
            rate: '',
            tariffId: null,
            tariffData: null,
        });
    }, []);

    const resetAll = useCallback(() => {
        setScenarioName("");
        setSiteName("");
        setStreet("");
        setCity("");
        setState("");
        setZipCode("");
        setSiteDescription("");
        setFacilityType("commercial");
        setUtilityName("");
        setServiceClass("commercial");
        setOffGrid(false);
        resetSolarConfig();
        resetBatteryConfig();
        resetGeneratorConfig();
        resetTariffConfig();
        setInputMode("ai_assisted");
        setAiExtractionComplete(false);
        setAiExtractedData(null);
        setError("");
    }, [resetSolarConfig, resetBatteryConfig, resetGeneratorConfig, resetTariffConfig]);

    // ========== Serialize/Deserialize for Persistence ==========

    const serializeFormState = useCallback(() => {
        return {
            site: siteConfig,
            solar: solarConfig,
            battery: batteryConfig,
            generator: generatorConfig,
            tariff: tariffConfig,
            commercialAssumptions,
            dealAnalysisConfig,
            inputMode,
            aiExtractionComplete,
            aiExtractedData,
            calculationMethod,
        };
    }, [siteConfig, solarConfig, batteryConfig, generatorConfig, tariffConfig, commercialAssumptions, dealAnalysisConfig, inputMode, aiExtractionComplete, aiExtractedData, calculationMethod]);

    const loadFormState = useCallback((savedState) => {
        if (!savedState) return;

        // Site config
        if (savedState.site) {
            setScenarioName(savedState.site.scenarioName || "");
            setSiteName(savedState.site.siteName || "");
            setStreet(savedState.site.street || savedState.site.siteAddress || ""); // Backward compat
            setCity(savedState.site.city || "");
            setState(savedState.site.state || "");
            setZipCode(savedState.site.zipCode || savedState.site.location || ""); // Backward compat
            setSiteDescription(savedState.site.siteDescription || "");
            setFacilityType(savedState.site.facilityType || "commercial");
            setUtilityName(savedState.site.utilityName || "");
            setServiceClass(savedState.site.serviceClass || "commercial");
            setOffGrid(savedState.site.offGrid || false);
        }

        // Solar config
        if (savedState.solar) {
            setUsePv(savedState.solar.usePv || false);
            setSizingMode(savedState.solar.sizingMode || "prescribed");
            setPvMaxKw(savedState.solar.pvMaxKw || "");
            setPvCost(savedState.solar.pvCost || String(DEFAULT_PV_COST_PER_KW));
            setPvExportMode(savedState.solar.pvExportMode || "none");
        }

        // Battery config
        if (savedState.battery) {
            setUseStorage(savedState.battery.useStorage || false);
            setBatterySizingMode(savedState.battery.batterySizingMode || "prescribed");
            setBatteries(savedState.battery.batteries || [{ id: 1, name: "Battery 1", kw: "", kwh: "" }]);
            setStorageMaxKw(savedState.battery.storageMaxKw || "");
            setStorageMaxKwh(savedState.battery.storageMaxKwh || "");
            setUseGenLoadBank(savedState.battery.useGenLoadBank || false);
            setBatteryReservePercent(savedState.battery.batteryReservePercent || "20");
            setBessChargeSource(savedState.battery.bessChargeSource || "pv_and_grid");
        }

        // Generator config
        if (savedState.generator) {
            setUseGenerator(savedState.generator.useGenerator || false);
            setGeneratorSizingMode(savedState.generator.generatorSizingMode || "prescribed");
            setGeneratorMaxKw(savedState.generator.generatorMaxKw || "");
            setGeneratorFuelCostPerGallon(savedState.generator.generatorFuelCostPerGallon || "3");
            setGeneratorFuelCostPerMmbtu(savedState.generator.generatorFuelCostPerMmbtu || "6");
            setGeneratorFuelType(savedState.generator.generatorFuelType || "diesel");
            setGeneratorExportMode(savedState.generator.generatorExportMode || "none");
            setGeneratorRunHoursLimit(savedState.generator.generatorRunHoursLimit || "");
            setGeneratorUptimePercent(savedState.generator.generatorUptimePercent || "96.5");
            setGeneratorThermalEfficiency(savedState.generator.generatorThermalEfficiency || "");
            setGenerators(savedState.generator.generators || []); // Multi-generator array
            setGasTariffId(savedState.generator.gasTariffId || null);
            setGasTariffData(savedState.generator.gasTariffData || null);
        }

        // Tariff config
        if (savedState.tariff) {
            setTariffs(savedState.tariff.tariffs || []);
            setTariffLocation(savedState.tariff.tariffLocation || null);
            setSelectedTariffData(savedState.tariff.selectedTariffData || null);
            setCustomTariffData(savedState.tariff.customTariffData || null);
            setCaDerConfig(savedState.tariff.caDerConfig || null);
            // Export compensation config
            if (savedState.tariff.exportConfig) {
                setExportConfig(savedState.tariff.exportConfig);
            }
            setUseManualRate(savedState.tariff.useManualRate || false);
            setRateStructureType(savedState.tariff.rateStructureType || "blended");
            setBlendedEnergyRate(savedState.tariff.blendedEnergyRate || "0.12");
            setBlendedDemandRate(savedState.tariff.blendedDemandRate || "15.0");
            setMonthlyFixedCharge(savedState.tariff.monthlyFixedCharge || "25.0");
            setTouPeriods(savedState.tariff.touPeriods || DEFAULT_TOU_PERIODS);
            setUseMonthlyRates(savedState.tariff.useMonthlyRates || false);
            setMonthlyFixedCharges(savedState.tariff.monthlyFixedCharges || Array(12).fill("25.0"));
            setTieredRates(savedState.tariff.tieredRates || DEFAULT_TIERED_RATES);
            setTieredDemandRate(savedState.tariff.tieredDemandRate || "6.03");
        }

        // Other state
        if (savedState.inputMode) setInputMode(savedState.inputMode);
        if (savedState.aiExtractionComplete !== undefined) setAiExtractionComplete(savedState.aiExtractionComplete);
        if (savedState.aiExtractedData) setAiExtractedData(savedState.aiExtractedData);
        if (savedState.calculationMethod) setCalculationMethod(savedState.calculationMethod);

        // Commercial assumptions
        if (savedState.commercialAssumptions) {
            setCommercialAssumptions(savedState.commercialAssumptions);
        }

        // Deal analysis config
        if (savedState.dealAnalysisConfig) {
            setDealAnalysisConfig(savedState.dealAnalysisConfig);
        }
    }, []);

    return {
        // Site config
        scenarioName, setScenarioName,
        siteName, setSiteName,
        street, setStreet,
        city, setCity,
        state, setState,
        zipCode, setZipCode,
        siteDescription, setSiteDescription,
        facilityType, setFacilityType,
        utilityName, setUtilityName,
        serviceClass, setServiceClass,
        offGrid, setOffGrid,

        // Solar config
        usePv, setUsePv,
        sizingMode, setSizingMode,
        pvMaxKw, setPvMaxKw,
        pvCost, setPvCost,
        pvExportMode, setPvExportMode,

        // Battery config
        useStorage, setUseStorage,
        batterySizingMode, setBatterySizingMode,
        batteries, setBatteries,
        storageMaxKw, setStorageMaxKw,
        storageMaxKwh, setStorageMaxKwh,
        useGenLoadBank, setUseGenLoadBank,
        batteryReservePercent, setBatteryReservePercent,
        bessChargeSource, setBessChargeSource,

        // Generator config
        useGenerator, setUseGenerator,
        generatorSizingMode, setGeneratorSizingMode,
        generatorMaxKw, setGeneratorMaxKw,
        generatorFuelCostPerGallon, setGeneratorFuelCostPerGallon,
        generatorFuelCostPerMmbtu, setGeneratorFuelCostPerMmbtu,
        generatorFuelType, setGeneratorFuelType,
        generatorExportMode, setGeneratorExportMode,
        generatorRunHoursLimit, setGeneratorRunHoursLimit,
        generatorUptimePercent, setGeneratorUptimePercent,
        generatorThermalEfficiency, setGeneratorThermalEfficiency,
        generators, setGenerators, // Multi-generator array
        gasTariffId, setGasTariffId,
        gasTariffData, setGasTariffData,

        // Tariff config
        tariffs, setTariffs,
        tariffLocation, setTariffLocation,
        selectedTariffData, setSelectedTariffData,
        customTariffData, setCustomTariffData,
        caDerConfig, setCaDerConfig,
        exportConfig, setExportConfig, // NEM 3.0 export compensation
        commercialAssumptions, setCommercialAssumptions,
        dealAnalysisConfig, setDealAnalysisConfig,
        useManualRate, setUseManualRate,
        rateStructureType, setRateStructureType,
        blendedEnergyRate, setBlendedEnergyRate,
        blendedDemandRate, setBlendedDemandRate,
        monthlyFixedCharge, setMonthlyFixedCharge,
        touPeriods, setTouPeriods,
        useMonthlyRates, setUseMonthlyRates,
        monthlyFixedCharges, setMonthlyFixedCharges,
        tieredRates, setTieredRates,
        tieredDemandRate, setTieredDemandRate,

        // AI mode
        inputMode, setInputMode,
        aiExtractionComplete, setAiExtractionComplete,
        aiExtractedData, setAiExtractedData,
        aiSuggestedTariffs, setAiSuggestedTariffs,

        // UI state
        tab, setTab,
        renameDialogOpen, setRenameDialogOpen,
        deleteDialogOpen, setDeleteDialogOpen,
        tempScenarioName, setTempScenarioName,
        saveDialogOpen, setSaveDialogOpen,
        modelName, setModelName,
        snackbar, setSnackbar,
        error, setError,
        calculationMethod, setCalculationMethod,
        savedModels, setSavedModels,
        selectedModel, setSelectedModel,

        // Grouped config objects
        siteConfig,
        solarConfig,
        batteryConfig,
        generatorConfig,
        tariffConfig,

        // Reset functions
        resetSolarConfig,
        resetBatteryConfig,
        resetGeneratorConfig,
        resetTariffConfig,
        resetAll,

        // Persistence
        serializeFormState,
        loadFormState,

        // Constants
        DEFAULT_PV_COST_PER_KW,
        DEFAULT_BATTERY_COST_PER_KWH,
        DEFAULT_GENERATOR_COST_PER_KW,
        DEFAULT_ANNUAL_KWH,
        DEFAULT_FINANCIAL,
    };
}

export default useScenarioForm;
